import React from "react";
import {
  Box, TextField, Typography, Slider, Button,
  Accordion, AccordionSummary, AccordionDetails,
  Chip, Divider, Grid2 as Grid,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import pb from "../pocketBase";
import { toast } from "react-toastify";
import { CTM_Colors, IFeedbackData } from "../Helper";
import ConfirmDialog from "./Dialog/ConfirmDialog";

interface FeedbackComponentProps {
  runId: string;
  initialFeedback?: IFeedbackData | null;
}

const DEFAULT_FEEDBACK: IFeedbackData = {
  comment: "",
  grip: 5,
  limit_Balance: 0,
  control_corner_entry: 5,
  control_corner_apex: 5,
  control_corner_exit: 5,
  stability_entry: 5,
  stability_apex: 5,
  stability_exit: 5,
  braking_stability: 5,
  braking_controlability: 5,
  throttle_stability: 5,
  throttle_controlability: 5,
};

// Returns a color based on 0–10 value: red → yellow → green
const scoreColor = (val: number, min = 0, max = 10): string => {
  const ratio = (val - min) / (max - min);
  if (ratio >= 0.7) return '#4caf50';
  if (ratio >= 0.4) return '#ff9800';
  return '#f44336';
};

const ScoreChip: React.FC<{ value: number; min?: number; max?: number }> = ({ value, min = 0, max = 10 }) => (
  <Chip
    label={value}
    size="small"
    sx={{
      fontWeight: 'bold',
      fontSize: '0.75rem',
      minWidth: 32,
      backgroundColor: scoreColor(value, min, max),
      color: '#fff',
    }}
  />
);

const SummaryScores: React.FC<{ feedback: IFeedbackData }> = ({ feedback }) => {
  const items = [
    { label: 'Grip', value: feedback.grip },
    { label: 'Ctrl', value: (feedback.control_corner_entry + feedback.control_corner_apex + feedback.control_corner_exit) / 3 },
    { label: 'Stab', value: (feedback.stability_entry + feedback.stability_apex + feedback.stability_exit) / 3 },
    { label: 'Brake', value: (feedback.braking_stability + feedback.braking_controlability) / 2 },
    { label: 'Throttle', value: (feedback.throttle_stability + feedback.throttle_controlability) / 2 },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 1.5, ml: 2, flexWrap: 'wrap' }}>
      {items.map(({ label, value }) => (
        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>{label}</Typography>
          <ScoreChip value={Math.round(value * 10) / 10} />
        </Box>
      ))}
    </Box>
  );
};

interface SliderRowProps {
  name: keyof IFeedbackData;
  label: string;
  value: number;
  onChange: (name: keyof IFeedbackData, val: number) => void;
  min?: number;
  max?: number;
  marks?: { value: number; label: string }[];
}

const SliderRow: React.FC<SliderRowProps> = ({ name, label, value, onChange, min = 0, max = 10, marks }) => (
  <Box sx={{ px: 1 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
      <ScoreChip value={value} min={min} max={max} />
    </Box>
    <Slider
      value={value}
      onChange={(_, val) => onChange(name, val as number)}
      min={min}
      max={max}
      step={1}
      marks={marks ?? true}
      valueLabelDisplay="auto"
      sx={{ color: scoreColor(value, min, max), py: 0.5 }}
    />
  </Box>
);

interface CategoryBoxProps {
  title: string;
  color: string;
  children: React.ReactNode;
}

const CategoryBox: React.FC<CategoryBoxProps> = ({ title, color, children }) => (
  <Box sx={{
    border: `1px solid ${color}40`,
    borderRadius: 2,
    p: 1.5,
    height: '100%',
  }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color, mb: 1.5, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const DriverFeedbackForm: React.FC<FeedbackComponentProps> = ({ runId, initialFeedback }) => {
  const [feedback, setFeedback] = React.useState<IFeedbackData>(initialFeedback ?? DEFAULT_FEEDBACK);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (initialFeedback) setFeedback(initialFeedback);
  }, [initialFeedback]);

  const handleChange = (name: keyof IFeedbackData, val: number) => {
    setFeedback((prev) => ({ ...prev, [name]: val }));
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const existing = await pb.collection("DriverFeedback").getFirstListItem(`run = "${runId}"`).catch(() => null);
      if (existing) {
        await pb.collection("DriverFeedback").update(existing.id, { ...feedback, run: runId });
      } else {
        await pb.collection("DriverFeedback").create({ ...feedback, run: runId });
      }
      toast.success("Feedback saved");
    } catch {
      toast.error("Failed to save feedback");
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const existing = await pb.collection("DriverFeedback").getFirstListItem(`run = "${runId}"`).catch(() => null);
      if (existing) {
        await pb.collection("DriverFeedback").delete(existing.id);
        setFeedback(DEFAULT_FEEDBACK);
        toast.success("Feedback deleted");
      } else {
        toast.info("No saved feedback to delete");
      }
    } catch {
      toast.error("Failed to delete feedback");
    } finally {
      setConfirmDialogOpen(false);
    }
  };

  return (
    <>
      <Accordion defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
            <Typography variant="h6" sx={{ mr: 1 }}>Driver Feedback</Typography>
            <SummaryScores feedback={feedback} />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>

          {/* Balance — full width, bipolar */}
          <CategoryBox title="Overall" color={CTM_Colors.ctm_green}>
            <SliderRow
              name="grip" label="Grip" value={feedback.grip}
              onChange={handleChange}
            />
            <Box sx={{ px: 1, mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Balance</Typography>
                <Chip
                  label={feedback.limit_Balance > 0 ? `O/S +${feedback.limit_Balance}` : feedback.limit_Balance < 0 ? `U/S ${feedback.limit_Balance}` : 'Neutral'}
                  size="small"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    backgroundColor: Math.abs(feedback.limit_Balance) <= 3 ? '#4caf50' : Math.abs(feedback.limit_Balance) <= 6 ? '#ff9800' : '#f44336',
                    color: '#fff',
                  }}
                />
              </Box>
              <Slider
                value={feedback.limit_Balance}
                onChange={(_, val) => handleChange('limit_Balance', val as number)}
                min={-10} max={10} step={1}
                marks={[{ value: -10, label: 'U/S' }, { value: 0, label: 'Neutral' }, { value: 10, label: 'O/S' }]}
                valueLabelDisplay="auto"
                sx={{ py: 0.5 }}
              />
            </Box>
          </CategoryBox>

          <Divider sx={{ my: 2 }} />

          {/* 2-column grid for the categories */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CategoryBox title="Corner Control" color="#1976d2">
                <SliderRow name="control_corner_entry" label="Entry" value={feedback.control_corner_entry} onChange={handleChange} />
                <SliderRow name="control_corner_apex" label="Apex" value={feedback.control_corner_apex} onChange={handleChange} />
                <SliderRow name="control_corner_exit" label="Exit" value={feedback.control_corner_exit} onChange={handleChange} />
              </CategoryBox>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CategoryBox title="Stability" color="#7b1fa2">
                <SliderRow name="stability_entry" label="Entry" value={feedback.stability_entry} onChange={handleChange} />
                <SliderRow name="stability_apex" label="Apex" value={feedback.stability_apex} onChange={handleChange} />
                <SliderRow name="stability_exit" label="Exit" value={feedback.stability_exit} onChange={handleChange} />
              </CategoryBox>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CategoryBox title="Braking" color="#d32f2f">
                <SliderRow name="braking_stability" label="Stability" value={feedback.braking_stability} onChange={handleChange} />
                <SliderRow name="braking_controlability" label="Controllability" value={feedback.braking_controlability} onChange={handleChange} />
              </CategoryBox>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CategoryBox title="Throttle" color="#e65100">
                <SliderRow name="throttle_stability" label="Stability" value={feedback.throttle_stability} onChange={handleChange} />
                <SliderRow name="throttle_controlability" label="Controllability" value={feedback.throttle_controlability} onChange={handleChange} />
              </CategoryBox>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Driver Comment"
            value={feedback.comment}
            onChange={(e) => setFeedback((prev) => ({ ...prev, comment: e.target.value }))}
          />

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" color="error" onClick={() => setConfirmDialogOpen(true)} size="small">
              Delete Feedback
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              size="small"
              sx={{ backgroundColor: CTM_Colors.ctm_green }}
            >
              {isSaving ? 'Saving…' : 'Save Feedback'}
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      <ConfirmDialog
        open={confirmDialogOpen}
        title="Delete Driver Feedback"
        message="Are you sure you want to delete this driver feedback?"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </>
  );
};

export default DriverFeedbackForm;
